import { BehaviorSubject, takeUntil } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

// Libraries

import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';

// Service
import { JobService } from '../../../../job/services/job.service';
import { CoreService } from '../../../../../shared/services';
import { SpinnerService } from '../../../../../shared/components/spinner/services/spinner.service';

// Models
import { EmailJdrModel } from '../../../../../dialogs/email-jdr-dialog/email-jdr-dialog.component';
import { AppCleanup } from '../../../../../shared/models';
import { JobDailyReportDto } from '../models/job-daily-report-dto';
import { JobTypeChannelDto } from '../models/job-type-channel-dto';

// Store
import { JobActions } from '../../../state/job.action';

// Enums
import { StatusType } from '../../../../../shared/enums';
import { ChangeType } from '../../../enums/change-type.enum';

// Helpers
import { UtilityHelper } from '../../../../../shared/helpers';

@Injectable({
	providedIn: 'root',
})
export class DailyReportService extends AppCleanup {
	private dailyReportUrl: string;
	private pdfDownloadUrl: string;
	private jdrEmailUrl: string;
	protected dailyDashboardSizeChangeVar: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
	dailyDashboardSizeChange$ = this.dailyDashboardSizeChangeVar.asObservable();
	private baseUrl: string;

	currentJobDailyReport: JobDailyReportDto;
	protected currentJobDailyReportVar: BehaviorSubject<JobDailyReportDto> = new BehaviorSubject<JobDailyReportDto>(null);
	currentJobDailyReport$ = this.currentJobDailyReportVar.asObservable();

	protected currentJobDailyReportChangedVar: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
	currentJobDailyReportChanged$ = this.currentJobDailyReportChangedVar.asObservable();

	constructor(
		private http: HttpClient,
		private coreService: CoreService,
		private jobService: JobService,
		private translateService: TranslateService,
		private store: Store,
		private spinnerService: SpinnerService
	) {
		super();
		this.baseUrl = this.apiUrl;
		this.dailyReportUrl = `${this.apiUrl}api/dailyreport`;
		this.pdfDownloadUrl = `${this.apiUrl}api/pdfdownload`;
		this.jdrEmailUrl = `${this.apiUrl}api/jdrEmail`;
	}

	getJobDailyReports(key: string) {
		let job = this.jobService.findJob(key);
		return job?.jobDailyReports;
	}

	getJobDailyReport(key: string, dailyReportId: number): JobDailyReportDto {
		let job = this.jobService.findJob(key);
		return job?.jobDailyReports.find((jobDailyReport) => jobDailyReport.id === dailyReportId);
	}

	getAndSetJobDailyReport(key: string, dailyReportId: number) {
		let dailyReport = this.getJobDailyReport(key, dailyReportId);
		this.setCurrentJobDailyReport(dailyReport);
	}

	addJobDailyReport(key: string, jobDailyReport: JobDailyReportDto) {
		return this.http.post<JobDailyReportDto>(`${this.dailyReportUrl}/${key}`, jobDailyReport);
	}

	updateDailyInfo(jobDailyReport: JobDailyReportDto) {
		this.spinnerService.showAppSpinner();
		this._updateDailyInfo(jobDailyReport.job.key, jobDailyReport)
			.pipe(takeUntil(this.destroy))
			.subscribe((response) => {
				if (response) {
					this.spinnerService.hideAppSpinner();
					this.modifyDailyReportRecord(jobDailyReport.job.key, jobDailyReport, false);
				}
			});
	}

	private _updateDailyInfo(key: string, jobDailyReport: JobDailyReportDto) {
		return this.http.post<JobDailyReportDto>(`${this.dailyReportUrl}/${key}/dailyinfo`, jobDailyReport);
	}

	modifyDailyReportRecord(key: string, jobDailyReport: JobDailyReportDto, showToaster = true) {
		let job = this.jobService.findJob(key);

		if (job) {
			const foundDailyReportIndex: number = job.jobDailyReports.findIndex((jobDailyReport) => jobDailyReport.id === jobDailyReport.id);

			if (foundDailyReportIndex >= 0) {
				job.jobDailyReports[foundDailyReportIndex] = jobDailyReport;
				this.store.dispatch(new JobActions.UpdateCachedJob(job));
				this.jobService.triggerJobChangeEvent(job, ChangeType.JOB_DAILY_REPORT);

				if (showToaster) {
					const message = `${this.translateService.instant('GENERIC.UPDATED', { name: this.translateService.instant('MODULE.DAILY_REPORT.TITLE_CAPITAL') })}`;
					this.coreService.toast({ message: message, type: StatusType.SUCCESS });
				}
			} else {
				this.addDailyReportRecord(key, jobDailyReport, showToaster);
			}

			if (jobDailyReport?.id === this.currentJobDailyReport?.id) {
				this.setCurrentJobDailyReport(jobDailyReport);
			}
		}
	}

	addDailyReportRecord(key: string, jobDailyReport: JobDailyReportDto, showToaster = true) {
		let job = this.jobService.findJob(key);
		if (job?.jobDailyReports) {
			job.jobDailyReports.push(jobDailyReport);
			this._sortJobDailyReports(job.jobDailyReports);
			if (showToaster) {
				const message = `${this.translateService.instant('GENERIC.CREATED', { name: this.translateService.instant('MODULE.DAILY_REPORT.TITLE_CAPITAL') })}`;
				this.coreService.toast({ message: message, type: StatusType.SUCCESS });
			}
			this.jobService.triggerJobChangeEvent(job, ChangeType.JOB_DAILY_REPORT);
		}
	}

	itemSizeChange() {
		this.dailyDashboardSizeChangeVar.next(true);
	}

	_getJobTypeChannels(key: string) {
		return this.http.get<JobTypeChannelDto[]>(`${this.dailyReportUrl}/jobtypechannels/${key}`);
	}

	downloadDailyReport(key: string) {
		const requestOptions: Object = {
			responseType: 'blob',
		};

		return this.http.get<any>(`${this.pdfDownloadUrl}/${key}`, requestOptions);
	}

	emailDailyReport(key: string, jdrEmail: EmailJdrModel) {
		return this.http.post<any>(`${this.jdrEmailUrl}/${key}`, jdrEmail);
	}

	private _sortJobDailyReports(jobDailyReports: JobDailyReportDto[]) {
		jobDailyReports.sort((a, b) => {
			return new Date(a.jobDate).getTime() - new Date(b.jobDate).getTime();
		});
	}

	setCurrentJobDailyReport(jobDailyReport: JobDailyReportDto) {
		let jobDailyReportCopy = UtilityHelper.deepCopy(jobDailyReport); // Need to do this, because jobDailyReport object is read only for some reason
		this.currentJobDailyReport = jobDailyReportCopy;
		this.currentJobDailyReportVar.next(jobDailyReportCopy);
		this.currentJobDailyReportChangedVar.next(true);
	}
}
